import type { GridProps } from '@chakra-ui/react'
import { forwardRef, Grid } from '@chakra-ui/react'
import type { DocumentLink, IInsuranceParams, Offer } from 'BrokerTsLib/src/dtos'
import { ContractDocType, getContractDocTypeName } from 'BrokerTsLib/src/enums'

import type { Document, DocumentSection } from '@/data/documents'
import { useGetAllDocuments } from '@/hooks/useDocuments'

import { useAuth } from '@/context/AuthContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { Loading } from '../Loading'
import { documentTypeColorMap } from './DocumentIcon'
import { DocumentsList } from './DocumentsList'
import { DocumentsSectionHeader } from './DocumentsSectionHeader'
import { InsuranceUpload } from './InsuranceUpload'

export interface DocumentsProps extends GridProps {
  insuranceParams: IInsuranceParams
  offer: Offer
}

const getDocumentType = (
  contractDocType: ContractDocType,
): Document['documentType'] => {
  switch (contractDocType) {
    case ContractDocType.ContractProposition:
      return 'ps'
    case ContractDocType.GreenCard:
      return 'zk'
    case ContractDocType.PreContractDocument:
      return 'zzj'
    case ContractDocType.LegalDocumentVpp:
      return 'vpp'
    case ContractDocType.IPID:
      return 'ipid'
    default:
      return 'pdf'
  }
}

const getDocument = (x: DocumentLink): Document => {
  return {
    name: x.description ?? getContractDocTypeName(x.type),
    url: x.url,
    documentType: getDocumentType(x.type),
    contractDocDtype: x.type,
  }
}

export const Documents = forwardRef(
  ({ insuranceParams, offer, sx, ...rest }: DocumentsProps, ref) => {
    const { app } = useApp()
    const { user } = useAuth()
    const { data, refetch, isLoading } = useGetAllDocuments(insuranceParams.generalParams.paramsId, offer.offerId)
    const allDocuments = data ?? []

    const nonLegalDocs =
      allDocuments.filter(
        (x) => !x.isLegalType && x.type !== ContractDocType.MergedDocuments,
      )
    const legalDocs =
      allDocuments.filter(
        (x) => x.isLegalType && x.type !== ContractDocType.MergedDocuments,
      )
    const nonLegalMergeDoc = allDocuments.find(
      (x) => !x.isLegalType && x.type === ContractDocType.MergedDocuments,
    )
    const legalMergeDoc = allDocuments.find(
      (x) => x.isLegalType && x.type === ContractDocType.MergedDocuments,
    )

    const documentsSections: DocumentSection[] = []

    if (nonLegalDocs.length > 0) {
      documentsSections.push({
        title: 'Hlavní dokumenty k pojistné smlouvě.',
        mergedDoc: nonLegalMergeDoc ? getDocument(nonLegalMergeDoc) : undefined,
        items: nonLegalDocs
          .map((x) => getDocument(x))
          .sort((a, b) =>
            documentTypeColorMap[a.documentType]!.localeCompare(
              documentTypeColorMap[b.documentType]!,
            ),
          ),
      })
    }

    if (legalDocs.length > 0) {
      documentsSections.push({
        title: 'Právní dokumenty k pojištění.',
        mergedDoc: legalMergeDoc ? getDocument(legalMergeDoc) : undefined,
        items: legalDocs.map((x) => {
          return {
            name: x.description ?? getContractDocTypeName(x.type),
            url: x.url,
            documentType: getDocumentType(x.type),
            contractDocDtype: x.type,
          }
        }),
      })
    }

    return <>
      {isLoading && <Loading large={true} />}
      {!isLoading && (
        <Grid
          ref={ref}
          sx={{
            gap: {
              base: 16,
              lg: 8,
            },
            gridTemplateColumns: {
              base: '1fr',
              lg: '1fr 1fr',
            },
            pl: {
              base: 4,
              lg: 24,
            },
            pr: {
              base: 4,
              md: 0,
            },
            ...sx,
          }}
          {...rest}
        >
          {documentsSections?.map((documentsSection, i) => {
            return (
              <Grid key={i} sx={{ alignContent: 'start', gap: 8 }}>
                <DocumentsSectionHeader
                  title={documentsSection.title}
                  lastDownloadedAll={documentsSection.lastDownloadedAll}
                  mergedDoc={documentsSection.mergedDoc}
                />

                <DocumentsList items={documentsSection.items} />
              </Grid>
            )
          })}

          {app != Apps.leasing && user?.allowUploadInsuranceDocuments &&
            <InsuranceUpload
              offerId={offer.offerId}
              existingDocuments={allDocuments ?? []}
              onUpload={refetch}
            />
          }
        </Grid>
      )}
    </>
  },
)
